/** SCR-13 / 14 — Company Admin: team users. */
import { useState } from 'react';
import { PageHeader, Card, Modal, Field, EmptyState, Spinner, ErrorState, Badge } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { pushToast } from '../ui/uiSlice.js';
import { dateTime } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { UserStatusBadge, errorText, fieldErrors } from './partnerShared.jsx';

const BLANK = { first_name: '', last_name: '', email: '', mobile: '', designation: '', department: '' };

export default function PartnerTeamPage() {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const q = useQuery({ queryKey: ['partner', 'team'], queryFn: api.listTeam });
  const [modal, setModal] = useState(null); // { member? }
  const toast = (tone, message, detail) => dispatch(pushToast({ tone, message, detail }));
  const refresh = () => qc.invalidateQueries({ queryKey: ['partner', 'team'] });
  const status = useMutation({
    mutationFn: ({ id, s }) => api.setTeamUserStatus(id, s), onSuccess: () => { refresh(); toast('success', 'Status updated'); },
    onError: (e) => toast('danger', 'Could not change status', errorText(e)),
  });
  const resend = useMutation({
    mutationFn: (id) => api.resendInvitation(id), onSuccess: (r) => toast('success', r.sent ? 'Invitation sent' : 'Invitation created', r.sent ? undefined : r.activation_url ?? 'Email could not be delivered.'),
    onError: (e) => toast('danger', 'Could not resend', errorText(e)),
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const rows = q.data;
  return (
    <>
      <PageHeader title="Team users" subtitle="Add and manage the people who submit leads for your company">
        <button type="button" className="btn btn-add btn-sm-x" onClick={() => setModal({})}><i className="bi bi-person-plus me-1" aria-hidden="true" />Add member</button>
      </PageHeader>
      <Card bare>
        {rows.length === 0 ? <EmptyState icon="bi-people" title="No team members" message="Add a member to let them submit leads." /> : (
          <div className="table-x-wrap"><table className="table-x">
            <thead><tr><th>Name</th><th>Role</th><th>Designation</th><th>Status</th><th className="text-end">Leads</th><th>Last submission</th><th /></tr></thead>
            <tbody>{rows.map((m) => (
              <tr key={m.id}>
                <td><div className="fw-600">{m.full_name}</div><div className="t-xs text-muted">{m.email} · {m.mobile}</div></td>
                <td>{m.role === 'admin' ? <Badge tone="violet">Company admin</Badge> : 'Member'}</td>
                <td>{m.designation ?? '—'}{m.department ? <div className="t-xs text-muted">{m.department}</div> : null}</td>
                <td><UserStatusBadge status={m.status} /></td>
                <td className="text-end">{m.leads_submitted}</td>
                <td>{m.last_submission_at ? dateTime(m.last_submission_at) : '—'}</td>
                <td className="text-end text-nowrap">
                  <button type="button" className="btn btn-edit btn-sm-x me-1" onClick={() => setModal({ member: m })}>Edit</button>
                  {m.status === 'invited' && <button type="button" className="btn btn-edit btn-sm-x me-1" disabled={resend.isPending} onClick={() => resend.mutate(m.id)}>Resend invite</button>}
                  {m.status === 'active' && <button type="button" className="btn btn-delete btn-sm-x" onClick={() => status.mutate({ id: m.id, s: 'inactive' })}>Deactivate</button>}
                  {m.status === 'inactive' && <button type="button" className="btn btn-add btn-sm-x" onClick={() => status.mutate({ id: m.id, s: 'active' })}>Activate</button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <div className="t-xs text-muted mt-2">Deactivating a member blocks their login but keeps every lead they submitted. Leads can still be reassigned from the lead page.</div>
      <MemberModal modal={modal} onClose={() => setModal(null)} onSaved={(msg, detail) => { setModal(null); refresh(); toast('success', msg, detail); }} />
    </>
  );
}

function MemberModal({ modal, onClose, onSaved }) {
  const member = modal?.member;
  const [f, setF] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [seen, setSeen] = useState(null);
  const key = modal ? (member?.id ?? 'new') : null;
  if (modal && seen !== key) {
    setSeen(key); setErrors({});
    const [first, ...rest] = (member?.full_name ?? '').split(' ');
    setF(member ? { first_name: first, last_name: rest.join(' '), email: member.email, mobile: member.mobile ?? '', designation: member.designation ?? '', department: member.department ?? '' } : BLANK);
  }
  if (!modal && seen) setSeen(null);
  const save = useMutation({
    mutationFn: () => {
      const b = Object.fromEntries(Object.entries(f).filter(([, v]) => String(v).trim() !== '').map(([k, v]) => [k, String(v).trim()]));
      if (member) { delete b.email; return api.updateTeamUser(member.id, b); }
      return api.addTeamUser(b);
    },
    onSuccess: (r) => onSaved(member ? 'Member updated' : 'Member added', member ? undefined : (r.invitation?.sent ? 'Invitation emailed.' : r.invitation?.activation_url ?? 'Invitation created.')),
    onError: (e) => setErrors({ ...fieldErrors(e), _: errorText(e) }),
  });
  if (!modal) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const er = {};
    ['first_name', 'last_name', 'mobile'].forEach((k) => { if (!f[k].trim()) er[k] = 'Required'; });
    if (!member && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) er.email = 'Enter a valid email';
    setErrors(er);
    if (!Object.keys(er).length) save.mutate();
  };
  const fld = (k, label, req, props = {}) => (<div className="col-md-6"><Field label={label} required={req} error={errors[k]}><input className="form-control" value={f[k]} onChange={set(k)} {...props} /></Field></div>);
  return (
    <Modal open onClose={onClose} title={member ? 'Edit team member' : 'Add team member'}
      footer={(<><button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button><button type="submit" form="memberForm" className="btn btn-save btn-sm-x" disabled={save.isPending}>{save.isPending ? <><ButtonSpinner />Saving…</> : member ? 'Save' : 'Add & invite'}</button></>)}>
      <form id="memberForm" onSubmit={submit} noValidate>
        {errors._ && <div className="alert alert-danger py-2 t-sm">{errors._}</div>}
        <div className="row g-3">
          {fld('first_name', 'First name', true)}{fld('last_name', 'Last name', true)}
          {fld('email', 'Email', true, { type: 'email', disabled: Boolean(member) })}{fld('mobile', 'Mobile', true, { type: 'tel' })}
          {fld('designation', 'Designation')}{fld('department', 'Department')}
        </div>
        {!member && <div className="t-xs text-muted mt-3">An invitation email with an activation link is sent to the member.</div>}
      </form>
    </Modal>
  );
}
